// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import reportProductDataIssue from "@werner/App/Handlers/Products/ReportProductDataIssue";
import useCurrentPageHref from "@werner/App/Hooks/useCurrentPageHref";
import { GridContainerProps } from "@werner/MobiusOverrides/GridContainer/GridContainer";
// XNG-Changes: import twobuttonmodal from override
import Modal, { ModalPresentationProps } from "@werner/MobiusOverrides/Modal/Modal";
import { SharedProductPagesContexts } from "@werner/XDL/constants";
import DataIssues from "@werner/XDL/Icons/ProductDataIssues";
import { color, hideElementOnPrint, modalButtonsActionStyles } from "@werner/XDL/Utilities";
import { xValidateOnSubmit } from "@werner/XDL/Utilities/xValidate/xFieldValidator";
import * as React from "react";
import { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const StyledForm = styled.form``;

const mapDispatchToProps = {
    reportProductDataIssue,
};

interface OwnProps extends WidgetProps {
    fields: {
        margin: string;
    };
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

interface ReportDataIssueStyle {
    wrapper?: InjectableCss;
    link?: LinkPresentationProps;
    checkbox?: CheckboxPresentationProps;
    errorText?: TypographyPresentationProps;
    formContainer?: GridContainerProps;
    halfGridItem?: GridItemProps;
    fullGridItem?: GridItemProps;
    modalButtonsWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
}

const reportDataIssueStyle: ReportDataIssueStyle = {
    wrapper: {
        css: css`
            width: 100%;
            max-width: 215px;
            margin-top: 18px;
            margin: ${({ margin }: any) => (margin ? `${margin}` : 0)};
            padding: 18px;
            display: flex;
            align-items: center;
            background: ${color("primary", 0.05)};
            ${hideElementOnPrint}
        `,
    },
    link: {
        color: "secondary",
        typographyProps: {
            size: 15,
            weight: 500,
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        icon: {
            position: "left",
            iconProps: {
                src: DataIssues,

                size: 28,
                css: css`
                    margin-right: 14px;
                `,
            },
        },
    },
    errorText: {
        size: 15,
        weight: 600,
        css: css`
            color: ${color("danger")};
        `,
    },
    formContainer: {
        gap: 24,
    },
    halfGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    fullGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    submitButton: {
        ...modalButtonsActionStyles.submitButton,
    },
    cancelButton: {
        ...modalButtonsActionStyles.cancelButton,
    },
};

const styles = reportDataIssueStyle;

const topics: string[] = [
    "Image",
    "Description/Specs",
    "Part Number",
    "Pricing",
    "Search/Category",
    "Spec Sheets/Documents",
    "Other",
];
const formId = "reportDataIssueForm";

const ReportDataIssue: FC<Props> = ({ fields, reportProductDataIssue }) => {
    const toasterContext = useContext(ToasterContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
    const [email, setEmail] = useState("");
    const [topcisError, setTopcisError] = useState("");
    const [description, setDescription] = useState("");
    const topcisFieldMessage = translate("Select at Least one below to submit");
    const pageUrl = useCurrentPageHref();

    const handleChangeDescription = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(event.target.value);
    };
    const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value;
        setEmail(email);
    };

    const checkForErrors = () => {
        const topcisError = selectedTopics.length < 1 ? topcisFieldMessage : "";
        setTopcisError(topcisError);
        return !!topcisError;
    };

    const resetFields = () => {
        setSelectedTopics([]);
        setEmail("");
        setDescription("");
        setTopcisError("");
    };

    const handleFormSubmit: React.MouseEventHandler<HTMLButtonElement> = event => {
        event.preventDefault();
        const errors = xValidateOnSubmit(formId);
        const otherFieldsErrors = checkForErrors();
        if (errors.length > 0 || otherFieldsErrors) {
            return;
        }
        reportProductDataIssue({
            issueTypes: selectedTopics,
            email,
            comments: description,
            pageUrl,
            onSuccess: () => {
                setIsModalOpen(false);
                toasterContext.addToast({
                    body: siteMessage("Custom_Report_Product_Issue_Success"),
                    messageType: "success",
                });
            },
            onError: () => {
                setIsModalOpen(false);
                toasterContext.addToast({
                    body: siteMessage("Custom_Report_Product_Issue_Success"),
                    messageType: "danger",
                });
            },
        });
    };

    const handleSelectTopic = (topic: string) => {
        if (selectedTopics.includes(topic)) {
            const newSelectedTopics = selectedTopics.filter(t => t !== topic);
            const topcisError = newSelectedTopics.length < 1 ? topcisFieldMessage : "";
            setSelectedTopics(newSelectedTopics);
            setTopcisError(topcisError);
            return;
        }
        setSelectedTopics(selectedTopics.concat(topic));
        setTopcisError("");
    };

    const toggleModal = () => {
        resetFields();
        setIsModalOpen(!isModalOpen);
    };
    // XNG-Changes
    const reportDataIssueAction = (
        <>
            <Button {...styles.cancelButton} onClick={toggleModal}>
                {translate("Cancel")}
            </Button>
            <Button {...styles.submitButton} onClick={handleFormSubmit}>
                {translate("Submit")}
            </Button>
        </>
    );
    // XNG-Changes

    return (
        <>
            <StyledWrapper {...styles.wrapper} {...{ margin: fields.margin }}>
                <Link {...styles.link} onClick={toggleModal} aria-label="Report Product Data Issues">
                    {translate("Report Product Data Issues")}
                </Link>
            </StyledWrapper>
            {/* //XNG-Changes */}
            <Modal
                headline={translate("How Can We Improve This Page?")}
                isOpen={isModalOpen}
                handleClose={toggleModal}
                actions={reportDataIssueAction}
                {...styles.modal}
            >
                <StyledForm>
                    <GridContainer {...styles.formContainer} data-test-selector="moreInfoForm">
                        <GridItem {...styles.fullGridItem}>
                            {topcisError && <Typography {...styles.errorText}>{topcisError}</Typography>}
                            <CheckboxGroup>
                                {topics.map(topic => (
                                    <Checkbox
                                        {...styles.checkbox}
                                        checked={selectedTopics.includes(topic)}
                                        onChange={() => {
                                            handleSelectTopic(topic);
                                        }}
                                        key={topic}
                                    >
                                        {translate(topic)}
                                        <VisuallyHidden>{translate(topic)}</VisuallyHidden>
                                    </Checkbox>
                                ))}
                            </CheckboxGroup>
                        </GridItem>
                        <GridItem {...styles.fullGridItem}>
                            <TextArea
                                label={translate("Description")}
                                value={description}
                                onChange={handleChangeDescription}
                                data-test-selector="productShareYourMessage"
                                required={true}
                                {...{ formId }}
                            />
                        </GridItem>
                        <GridItem {...styles.fullGridItem}>
                            <TextField
                                label={translate("Email")}
                                type="email"
                                value={email}
                                onChange={handleChangeEmail}
                                required={false}
                                {...{ formId }}
                            />
                        </GridItem>

                        <GridItem {...styles.fullGridItem}>
                            <TextField
                                label={translate("Page Url")}
                                value={pageUrl}
                                type="url"
                                {...{ formId }}
                                disabled
                            />
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(ReportDataIssue),
    definition: {
        group: "Xngage  Custom" as any,
        displayName: "Report Data Issue",
        icon: "Link",
        allowedContexts: [...SharedProductPagesContexts, "ProductDetailsPage"] as any,
        fieldDefinitions: [
            {
                name: "margin",
                editorTemplate: "TextField",
                defaultValue: "30px 0px",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
